import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Alert, api, ApiError, Badge, Button, Card, fmtDate, ListPage, Spinner, useFetch, usePagedList } from '@greenrinse/ui';
import { NotificationDto } from './useNotifications';
import { isSubscribed, pushPermission, pushSupported, subscribeToPush, unsubscribeFromPush } from './push';

interface Preference { type: string; inApp: boolean; email: boolean; push: boolean; isDefault: boolean }

/**
 * Full inbox + per-type channel preferences + "enable push on this device".
 * `swUrl` = push-only service worker for apps without a PWA worker (admin/partner pass "/push-sw.js").
 */
export default function NotificationsPage({ swUrl }: { swUrl?: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [tab, setTab] = useState<'inbox' | 'preferences'>('inbox');
  const [unreadOnly, setUnreadOnly] = useState(false);
  const list = usePagedList<NotificationDto>('/api/notifications', { unreadOnly: unreadOnly || undefined }, 25);
  const markAllRead = async () => { await api('/api/notifications/read-all', { method: 'POST' }).catch(() => undefined); list.reload(); };

  const open = (n: NotificationDto) => {
    if (!n.isRead) void api(`/api/notifications/${n.id}/read`, { method: 'POST' }).catch(() => undefined).then(() => list.reload());
    if (n.link) navigate(n.link);
  };

  return (
    <div className="gr-stack">
      <div className="gr-row">
        <button type="button" className={`gr-btn ${tab === 'inbox' ? 'gr-btn--primary' : 'gr-btn--outline'}`} style={{ minHeight: 36 }} onClick={() => setTab('inbox')}>{t('notifications.inbox')}</button>
        <button type="button" className={`gr-btn ${tab === 'preferences' ? 'gr-btn--primary' : 'gr-btn--outline'}`} style={{ minHeight: 36 }} onClick={() => setTab('preferences')}>{t('notifications.preferences')}</button>
      </div>

      {tab === 'inbox' && (
        <ListPage<NotificationDto>
          title={t('notifications.title')} state={list} rowKey={(n) => n.id} onRowClick={open}
          empty={t('notifications.empty')}
          actions={<Button variant="outline" style={{ minHeight: 36 }} onClick={() => void markAllRead()}>{t('notifications.mark_all_read')}</Button>}
          filters={<label className="gr-row" style={{ gap: 6 }}><input type="checkbox" checked={unreadOnly} onChange={(e) => setUnreadOnly(e.target.checked)} /> {t('notifications.unread_only')}</label>}
          columns={[
            { key: 'state', header: '', width: 24, render: (n) => (n.isRead ? '' : <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 99, background: 'var(--gr-primary)' }} />) },
            { key: 'title', header: t('notifications.title'), render: (n) => <><strong style={{ fontWeight: n.isRead ? 500 : 700 }}>{n.title}</strong><div className="gr-muted" style={{ fontSize: 13 }}>{n.body}</div></> },
            { key: 'type', header: t('notifications.type'), render: (n) => <Badge>{t(`notifications.types.${n.type}`, { defaultValue: n.type })}</Badge> },
            { key: 'when', header: t('notifications.when'), render: (n) => fmtDate(n.createdAtUtc), align: 'end' },
          ]}
        />
      )}

      {tab === 'preferences' && <PreferencesPanel swUrl={swUrl} />}
    </div>
  );
}

export function PreferencesPanel({ swUrl }: { swUrl?: string }) {
  const { t } = useTranslation();
  const prefs = useFetch<Preference[]>('/api/notifications/preferences');
  const [rows, setRows] = useState<Preference[]>([]);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<{ tone: 'success' | 'error' | 'info'; text: string } | null>(null);
  useEffect(() => { if (prefs.data) setRows(prefs.data); }, [prefs.data]);

  const toggle = (type: string, key: 'inApp' | 'email' | 'push') => setRows((r) => r.map((p) => (p.type === type ? { ...p, [key]: !p[key], isDefault: false } : p)));

  const save = async () => {
    setSaving(true); setMsg(null);
    try { await api('/api/notifications/preferences', { method: 'PUT', body: { preferences: rows } }); setMsg({ tone: 'success', text: t('settings.saved') }); }
    catch (e) { setMsg({ tone: 'error', text: e instanceof ApiError ? e.message : t('common.error_generic') }); }
    finally { setSaving(false); }
  };

  const test = async (channel: 'Email' | 'Push' | 'InApp') => {
    setMsg(null);
    try { await api('/api/notifications/test', { method: 'POST', body: { channel } }); setMsg({ tone: 'info', text: t('notifications.test_sent', { channel }) }); }
    catch (e) { setMsg({ tone: 'error', text: e instanceof ApiError ? e.message : t('common.error_generic') }); }
  };

  if (prefs.loading) return <Spinner />;
  return (
    <div className="gr-stack">
      <PushCard swUrl={swUrl} />
      <Card small style={{ padding: 0 }}>
        <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--gr-border)' }}><strong>{t('notifications.channels_per_type')}</strong><div className="gr-muted" style={{ fontSize: 13 }}>{t('notifications.channels_hint')}</div></div>
        <table className="gr-table">
          <thead><tr><th>{t('notifications.type')}</th><th style={{ textAlign: 'center' }}>{t('notifications.in_app')}</th><th style={{ textAlign: 'center' }}>{t('notifications.email')}</th><th style={{ textAlign: 'center' }}>{t('notifications.push')}</th></tr></thead>
          <tbody>
            {rows.map((p) => (
              <tr key={p.type}>
                <td>{t(`notifications.types.${p.type}`, { defaultValue: p.type })}<div className="gr-muted" style={{ fontSize: 12 }}>{t(`notifications.type_hints.${p.type}`, { defaultValue: '' })}</div></td>
                {(['inApp', 'email', 'push'] as const).map((k) => (
                  <td key={k} style={{ textAlign: 'center' }}><input type="checkbox" aria-label={`${p.type} ${k}`} checked={p[k]} onChange={() => toggle(p.type, k)} style={{ width: 20, height: 20 }} /></td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        <div className="gr-row" style={{ padding: 12, gap: 8 }}>
          <Button loading={saving} onClick={() => void save()}>{t('common.save')}</Button>
          <span className="gr-topbar__spacer" />
          <span className="gr-muted" style={{ fontSize: 13 }}>{t('notifications.send_test')}:</span>
          <Button variant="outline" style={{ minHeight: 32 }} onClick={() => void test('InApp')}>{t('notifications.in_app')}</Button>
          <Button variant="outline" style={{ minHeight: 32 }} onClick={() => void test('Email')}>{t('notifications.email')}</Button>
          <Button variant="outline" style={{ minHeight: 32 }} onClick={() => void test('Push')}>{t('notifications.push')}</Button>
        </div>
        {msg && <div style={{ padding: '0 12px 12px' }}><Alert tone={msg.tone}>{msg.text}</Alert></div>}
      </Card>
    </div>
  );
}

/** "Enable push on this device" card with the honest state of browser support / permission / server configuration. */
export function PushCard({ swUrl }: { swUrl?: string }) {
  const { t } = useTranslation();
  const status = useFetch<{ enabled: boolean; publicKey: string | null; subscriptions: number }>('/api/notifications/push');
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  useEffect(() => { void isSubscribed(swUrl).then(setSubscribed); }, [swUrl]);

  const supported = pushSupported();
  const permission = pushPermission();
  const standaloneHint = /iPhone|iPad/.test(navigator.userAgent) && !(window.matchMedia('(display-mode: standalone)').matches);

  const enable = async () => {
    setBusy(true); setNote(null);
    try {
      const r = await subscribeToPush(swUrl);
      if (r === 'subscribed') { setSubscribed(true); status.reload(); }
      else setNote(t(`notifications.push_${r}`));
    } catch (e) { setNote(e instanceof Error ? e.message : String(e)); }
    finally { setBusy(false); }
  };
  const disable = async () => { setBusy(true); try { await unsubscribeFromPush(swUrl); setSubscribed(false); status.reload(); } finally { setBusy(false); } };

  return (
    <Card small>
      <div className="gr-row">
        <div style={{ flex: 1, minWidth: 200 }}>
          <strong>🔔 {t('notifications.push_this_device')}</strong>
          <div className="gr-muted" style={{ fontSize: 13 }}>
            {!supported && t('notifications.push_unsupported')}
            {supported && status.data && !status.data.enabled && t('notifications.push_disabled')}
            {supported && status.data?.enabled && (subscribed ? t('notifications.push_on') : t('notifications.push_off'))}
            {supported && permission === 'denied' && ` · ${t('notifications.push_denied')}`}
            {standaloneHint && ` · ${t('notifications.push_ios_hint')}`}
          </div>
          {status.data && status.data.subscriptions > 0 && <div className="gr-muted" style={{ fontSize: 12 }}>{t('notifications.push_devices', { count: status.data.subscriptions })}</div>}
        </div>
        {supported && status.data?.enabled && (subscribed
          ? <Button variant="outline" loading={busy} onClick={() => void disable()}>{t('notifications.push_turn_off')}</Button>
          : <Button loading={busy} disabled={permission === 'denied'} onClick={() => void enable()}>{t('notifications.push_turn_on')}</Button>)}
      </div>
      {note && <div style={{ marginTop: 8 }}><Alert tone="info">{note}</Alert></div>}
    </Card>
  );
}
